import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { fmtBRL, fmtNumber } from "../lib/format";
import { useFilters, periodToDates } from "../lib/filters";
import { KpiTile } from "../components/intelligence/KpiTile";
import { CardMemoryTrigger } from "../components/card-memory/CardMemoryTrigger";
import { PageHeader } from "../components/PageHeader";
import { IconDocument, IconTruck, IconDollar, IconInfo, IconChevronRight } from "../components/icons";
import { LoadMoreBar, useProgressiveList } from "../components/LoadMoreRow";
import type { TransportVehicleCostDto } from "@bi/types";
import { QueryErrorBanner } from "../components/QueryErrorBanner";

/** "salario_comissao" → "Salário / Comissão" (rótulos legíveis de categoria/sub-balde). */
const LABELS: Record<string, string> = {
  salario_comissao: "Salário / Comissão",
  frota_geral: "Frota (geral)",
  outros_sem_placa: "Outros (sem placa)",
  ipva: "IPVA",
  manutencao: "Manutenção",
  combustivel: "Combustível",
  pedagio: "Pedágio",
  seguro: "Seguro",
  administrativo: "Administrativo",
  terceiros: "Terceiros",
  operacional: "Operacional",
  outros: "Outros",
};
const label = (s: string) => LABELS[s] ?? s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/** "outros" é o balde padrão do backend para conta financeira SEM tipo de despesa cadastrado. */
const UNKNOWN_CATEGORY = "outros";

// Composição do custo: verde = o que chegou a uma placa; os grupos sem placa
// em tons neutros/âmbar, para o olho ler "alocado × não alocado" primeiro.
const ALLOCATED_COLOR = "var(--brand)";
const BUCKET_COLORS: Record<string, string> = {
  administrativo: "#94A3B8",
  terceiros: "#F59E0B",
  frota_geral: "#3B82F6",
  operacional: "#14B8A6",
  outros_sem_placa: "#CBD5E1",
  estoque: "#A855F7",
  unresolved: "#EF4444",
};
const CATEGORY_COLORS = ["var(--brand)", "#3B82F6", "#F59E0B", "#14B8A6", "#A855F7", "#EF4444"];

/** Valor positivo que arredondaria pra "0,0%" vira "<0,1%" (não parece zero). */
const pctFmt = (v: number) =>
  v > 0 && v < 0.05 ? "<0,1%" : `${v.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

/** Barrinha de proporção usada nos KPIs e nas listas. */
function ShareBar({ pct, color }: { pct: number; color: string }) {
  return (
    <div className="h-1.5 rounded-full bg-surface-muted overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${Math.min(100, Math.max(0, pct))}%`, background: color }} />
    </div>
  );
}

export function VehicleCostPage() {
  const { filters } = useFilters();
  const { from, to } = periodToDates(filters.period, filters.customFrom, filters.customTo);

  const { data, isLoading, isError: dataError, refetch: dataRefetch } = useQuery({
    queryKey: ["transport", "vehicle-costs", from, to],
    queryFn: () =>
      api.get<TransportVehicleCostDto>(
        `/dashboards/transport/vehicle-costs?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      ),
    staleTime: 60_000,
  });

  const byCategory = data?.byCategory ?? [];
  const byPlate = data?.byPlate ?? [];
  const plateList = useProgressiveList(byPlate);
  const breakdown = data?.unallocated.semPlacaBreakdown ?? [];
  const allocated = byPlate.reduce((s, p) => s + p.total, 0);
  const naoAlocado = data ? data.unallocated.semPlaca + data.unallocated.estoque + data.unallocated.unresolved : 0;
  const total = data?.total ?? 0;
  const pctOf = (v: number) => (total > 0 ? (v / total) * 100 : 0);
  const maxPlate = Math.max(1, ...byPlate.map((p) => p.total));

  // Fatias da composição: alocado + cada grupo sem placa + estoque + não identificado.
  const composition = useMemo(() => {
    if (!data) return [];
    return [
      { key: "alocado", name: "Alocado a placas", value: allocated, color: ALLOCATED_COLOR, allocated: true },
      ...breakdown.map((b) => ({ key: b.bucket, name: label(b.bucket), value: b.total, color: BUCKET_COLORS[b.bucket] ?? "#CBD5E1", allocated: false })),
      ...(data.unallocated.estoque > 0 ? [{ key: "estoque", name: "Estoque / Imobilizado", value: data.unallocated.estoque, color: BUCKET_COLORS.estoque, allocated: false }] : []),
      ...(data.unallocated.unresolved > 0 ? [{ key: "unresolved", name: "Centro não identificado", value: data.unallocated.unresolved, color: BUCKET_COLORS.unresolved, allocated: false }] : []),
    ].filter((s) => s.value > 0);
  }, [data, breakdown, allocated]);

  const categoryTotal = byCategory.reduce((s, c) => s + c.total, 0);
  const unknownShare = categoryTotal > 0
    ? ((byCategory.find((c) => c.category === UNKNOWN_CATEGORY)?.total ?? 0) / categoryTotal) * 100
    : 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Frota · Custo por Placa"
        subtitle="Demonstrativo por conta financeira e centro de custo: custo por veículo e tipo de despesa"
      />

      {dataError && <QueryErrorBanner onRetry={() => { dataRefetch(); }} />}

      {/* KPIs. Sem o selo "Atenção" sempre ligado: custo positivo é o normal,
          não uma anomalia (mesmo critério já aplicado em Visão Geral/Combustível). */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <CardMemoryTrigger memory={{ cardKey: "custo-placa.total", from, to, expected: data?.total, title: "Custo Total" }}>
          <KpiTile label="Custo total" value={data ? data.totalFormatted : "—"}
            footnote="Rateio por centro de custo no período" loading={isLoading}
            visual={data ? <ShareBar pct={100} color="#D1D5DB" /> : undefined} />
        </CardMemoryTrigger>
        <CardMemoryTrigger memory={{ cardKey: "custo-placa.alocado", from, to, expected: data ? allocated : undefined, title: "Custo Alocado a Placas" }}>
          <KpiTile label="Alocado a placas" value={data ? fmtBRL(allocated) : "—"}
            footnote={data ? `${pctFmt(pctOf(allocated))} do total · ${fmtNumber(byPlate.length)} placas` : undefined} loading={isLoading}
            visual={data ? <ShareBar pct={pctOf(allocated)} color={ALLOCATED_COLOR} /> : undefined} />
        </CardMemoryTrigger>
        <CardMemoryTrigger memory={{ cardKey: "custo-placa.sem-placa", from, to, expected: data?.unallocated.semPlaca, title: "Sem Placa" }}>
          <KpiTile label="Sem placa" value={data ? fmtBRL(data.unallocated.semPlaca) : "—"}
            footnote={data ? `${pctFmt(pctOf(data.unallocated.semPlaca))} do total · administrativo, terceiros e geral` : undefined} loading={isLoading}
            visual={data ? <ShareBar pct={pctOf(data.unallocated.semPlaca)} color="#94A3B8" /> : undefined} />
        </CardMemoryTrigger>
        <CardMemoryTrigger memory={{ cardKey: "custo-placa.nao-alocado", from, to, expected: data ? naoAlocado : undefined, title: "Não Alocado (total)" }}>
          <KpiTile label="Não alocado (total)" value={data ? fmtBRL(naoAlocado) : "—"}
            footnote={data ? `${pctFmt(pctOf(naoAlocado))} do total · sem placa + estoque` : undefined} loading={isLoading}
            visual={data ? <ShareBar pct={pctOf(naoAlocado)} color="#94A3B8" /> : undefined} />
        </CardMemoryTrigger>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
        {/* Composição: a mensagem principal da tela é quanto do custo chega
            (ou não) a uma placa. Antes isso ficava espalhado em 2 KPIs e numa
            lista "sem placa" separada, sem nada mostrando a proporção. */}
        <div className="card overflow-hidden xl:col-span-3 flex flex-col">
          <div className="px-5 py-4 border-b border-surface-border flex items-center gap-2">
            <IconDocument className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-sm font-semibold text-gray-900">Para onde foi o custo</p>
              <p className="text-xs text-gray-400 mt-0.5">Quanto chegou a uma placa e como se divide o que ficou sem placa</p>
            </div>
          </div>
          <div className="p-5 flex-1">
            {isLoading ? (
              <div className="h-40 bg-gray-50 rounded-lg animate-pulse" />
            ) : composition.length === 0 ? (
              <p className="py-6 text-sm text-gray-400 text-center">Sem custo no período.</p>
            ) : (
              <>
                <div className="flex h-4 rounded-full overflow-hidden bg-surface-muted">
                  {composition.map((s) => (
                    <div key={s.key} title={`${s.name}: ${fmtBRL(s.value)} (${pctFmt(pctOf(s.value))})`}
                      style={{ width: `${pctOf(s.value)}%`, background: s.color }}
                      className="h-full border-r border-white last:border-r-0" />
                  ))}
                </div>
                <div className="mt-2 flex justify-between text-[11px] text-gray-400">
                  <span>Alocado a placas · {pctFmt(pctOf(allocated))}</span>
                  <span>Sem placa ou estoque · {pctFmt(pctOf(naoAlocado))}</span>
                </div>

                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                  {composition.map((s) => (
                    <div key={s.key} className={`flex items-center gap-2.5 rounded-lg px-2.5 py-2 ${s.allocated ? "bg-tm-50" : ""}`}>
                      <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: s.color }} />
                      <span className={`text-sm truncate ${s.allocated ? "font-semibold text-tm-800" : "text-gray-600"}`}>{s.name}</span>
                      <span className="ml-auto text-sm font-semibold text-gray-900 whitespace-nowrap">{fmtBRL(s.value)}</span>
                      <span className="w-12 text-right text-xs text-gray-400">{pctFmt(pctOf(s.value))}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="card overflow-hidden xl:col-span-2 flex flex-col">
          <div className="px-5 py-4 border-b border-surface-border flex items-center gap-2">
            <IconDollar className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-sm font-semibold text-gray-900">Custo por tipo de despesa</p>
              <p className="text-xs text-gray-400 mt-0.5">Pelo tipo cadastrado na conta financeira</p>
            </div>
          </div>
          <div className="p-5 space-y-3.5 flex-1">
            {isLoading ? (
              <div className="h-32 bg-gray-50 rounded-lg animate-pulse" />
            ) : byCategory.length === 0 ? (
              <p className="py-6 text-sm text-gray-400 text-center">Sem custo no período.</p>
            ) : byCategory.map((c, i) => {
              const share = categoryTotal > 0 ? (c.total / categoryTotal) * 100 : 0;
              return (
                <div key={c.category}>
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm text-gray-700 truncate">{label(c.category)}</span>
                    <span className="ml-auto text-sm font-semibold text-gray-900 whitespace-nowrap">{c.formatted}</span>
                    <span className="w-12 text-right text-xs text-gray-400">{pctFmt(share)}</span>
                  </div>
                  <div className="mt-1.5">
                    <ShareBar pct={share} color={c.category === UNKNOWN_CATEGORY ? "#CBD5E1" : CATEGORY_COLORS[i % CATEGORY_COLORS.length] ?? ALLOCATED_COLOR} />
                  </div>
                </div>
              );
            })}
          </div>
          {/* "Outros" não é um tipo de gasto: é o balde do backend para conta
              sem tipo cadastrado. Quando domina, a quebra acima diz pouco, e a
              tela explica o porquê em vez de deixar parecer um gasto real. */}
          {!isLoading && unknownShare >= 50 && (
            <div className="mx-5 mb-5 rounded-lg border border-warning-100 bg-warning-50 px-3 py-2.5 flex gap-2">
              <IconInfo className="w-4 h-4 text-warning-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-warning-700 leading-snug">
                {pctFmt(unknownShare)} do custo está em "Outros": são contas financeiras sem tipo de despesa cadastrado.
                Classificar essas contas faz esta quebra (e o custo por veículo) mostrar combustível, manutenção etc.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Ranking por placa: barra relativa à placa mais cara + % do alocado. */}
      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b border-surface-border flex flex-wrap items-center gap-2">
          <IconTruck className="w-4 h-4 text-gray-400" />
          <div>
            <p className="text-sm font-semibold text-gray-900">Custo por veículo</p>
            <p className="text-xs text-gray-400 mt-0.5">
              {data ? `${fmtNumber(byPlate.length)} placas com custo no período · ${fmtBRL(allocated)} alocados` : "Custo rateado para cada placa"}
            </p>
          </div>
        </div>
        {isLoading ? (
          <p className="px-5 py-6 text-sm text-gray-400 text-center">Carregando…</p>
        ) : byPlate.length === 0 ? (
          <p className="px-5 py-6 text-sm text-gray-400 text-center">Sem custo por placa no período (rateio não sincronizado?).</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-400 border-b border-surface-border">
                  <th className="pl-5 pr-2 py-2 font-medium w-10">#</th>
                  <th className="px-2 py-2 font-medium">Placa</th>
                  <th className="px-2 py-2 font-medium">Tipo de despesa</th>
                  <th className="px-2 py-2 font-medium w-[32%]">Participação no alocado</th>
                  <th className="pl-2 pr-5 py-2 font-medium text-right">Custo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {plateList.visible.map((p, i) => {
                  const share = allocated > 0 ? (p.total / allocated) * 100 : 0;
                  return (
                    <tr key={p.plate} className="text-gray-700 hover:bg-surface-muted/50">
                      <td className="pl-5 pr-2 py-2.5 text-xs font-bold text-gray-400">{i + 1}</td>
                      <td className="px-2 py-2.5">
                        <Link to={`/frota/placa/${encodeURIComponent(p.plate)}`}
                          className="inline-flex items-center gap-1 font-medium text-tm-700 hover:underline whitespace-nowrap">
                          {p.plate} <IconChevronRight className="w-3 h-3 opacity-60" />
                        </Link>
                      </td>
                      <td className="px-2 py-2.5">
                        <div className="flex flex-wrap gap-1">
                          {p.byCategory.slice(0, 3).map((c) => (
                            <span key={c.category} className="rounded-full bg-surface-muted px-2 py-0.5 text-[11px] text-gray-500 whitespace-nowrap">
                              {label(c.category)}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-2 py-2.5">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 rounded-full bg-surface-muted overflow-hidden">
                            <div className="h-full rounded-full bg-tm-400" style={{ width: `${(p.total / maxPlate) * 100}%` }} />
                          </div>
                          <span className="w-12 text-right text-xs text-gray-500">{pctFmt(share)}</span>
                        </div>
                      </td>
                      <td className="pl-2 pr-5 py-2.5 text-right font-semibold text-gray-900 whitespace-nowrap">{p.formatted}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {!isLoading && byPlate.length > 0 && <LoadMoreBar list={plateList} label="placas" />}
      </div>
    </div>
  );
}
